import { queryOptions } from '@tanstack/react-query';

import { fetchBackend } from './apiClient';

export type AwsRoleToCreate = Omit<AwsRole, 'id' | 'status'>;

type AwsRoleStatus = 'active' | 'inactive' | 'error';

export type AwsRole = {
  name: string;
  arn: string;
  externalId?: string;
  id: string;
  status: AwsRoleStatus;
};

export const awsRoleQueries = {
  all: () => ['aws-role'],
  byProject: (projectId: string) => [...awsRoleQueries.all(), projectId],
  list: (projectId: string) =>
    queryOptions({
      queryKey: [...awsRoleQueries.byProject(projectId), 'list'],
      queryFn: async () => listAwsRoles(projectId),
    }),
  details: (projectId: string) => [
    ...awsRoleQueries.byProject(projectId),
    'details',
  ],
  detail: (projectId: string, roleId: string) =>
    queryOptions({
      queryKey: [...awsRoleQueries.details(projectId), roleId],
      queryFn: async () => getAwsRole(projectId, roleId),
    }),
} as const;

export const upsertAwsRole = async (
  projectId: string,
  payload: AwsRoleToCreate,
  roleId?: string,
): Promise<AwsRoleToCreate> => {
  if (roleId !== undefined) {
    const updateResponse = await fetchBackend(
      `/aws-roles/${projectId}/${roleId}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );

    if (!updateResponse.ok) {
      throw new Error(`Error: ${updateResponse.statusText}`);
    }

    return payload;
  }

  const createResponse = await fetchBackend(`/aws-roles/${projectId}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!createResponse.ok) {
    throw new Error(`Error: ${createResponse.statusText}`);
  }

  return payload;
};

export const getAwsRole = async (
  projectId: string,
  roleId: string,
): Promise<AwsRole> => {
  const getResponse = await fetchBackend(`/aws-roles/${projectId}/${roleId}`, {
    method: 'GET',
  });

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as {
    awsRole: AwsRole;
  };

  return data.awsRole;
};

export const listAwsRoles = async (projectId: string): Promise<AwsRole[]> => {
  const getResponse = await fetchBackend(`/aws-roles/${projectId}`, {
    method: 'GET',
  });

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as {
    awsRoles: AwsRole[];
  };

  return data.awsRoles;
};

export const deleteAwsRole = async (
  projectId: string,
  roleId: string,
): Promise<void> => {
  const deleteResponse = await fetchBackend(
    `/aws-roles/${projectId}/${roleId}`,
    {
      method: 'DELETE',
    },
  );

  if (!deleteResponse.ok) {
    throw new Error(`Error: ${deleteResponse.statusText}`);
  }
};
